import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../config/api';
import { lightstoneCoverageRejectReason } from '../utils/lightstoneCoverage';
import { sanitizeRetrieveCopy, withPdfFallback } from '../utils/sanitizeRetrieveCopy';
import { sameCapturePlace } from '../utils/prospectCaptures';
import { cloudRunFetchBase } from '../utils/cloudRunBase';
import { CURRENT_PROPERTY_REPORT_SOURCE, propertyReportSourceError } from '../utils/propertyReportSource';
import LightstoneProgressBar from '../components/cma/LightstoneProgressBar';
import './LightstoneJobStack.css';

const LIGHTSTONE_FETCH_BASE = cloudRunFetchBase();
/** One Chromium login at a time. Extra addresses wait in the bottom-left stack. */
const MAX_PARALLEL = 1;
/** Client abort so a stuck retrieve does not sit for 10+ minutes with no error. */
const FETCH_TIMEOUT_MS = 180000;
const SESSION_KEY = 'ipm_ls_jobs';
const LightstoneRetrievalContext = createContext(null);

function newJobId() {
    return `ls_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = String(reader.result || '');
            const comma = result.indexOf(',');
            resolve(comma >= 0 ? result.slice(comma + 1) : result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

function jobIsActive(j) {
    return j && (j.status === 'queued' || j.status === 'suggesting' || j.status === 'fetching' || j.status === 'parsing');
}

function nearbyFromPayload(data) {
    if (!data || typeof data !== 'object') return [];
    if (Array.isArray(data.nearby) && data.nearby.length) return data.nearby;
    if (Array.isArray(data.hits) && data.hits.length) return data.hits;
    if (Array.isArray(data.suggestions) && data.suggestions.length) return data.suggestions;
    return Array.isArray(data.nearby) ? data.nearby : [];
}

export function lightstoneProgressLabel(job) {
    if (!job) return '';
    if (job.status === 'queued') return 'Waiting to retrieve…';
    if (job.status === 'suggesting') return 'Getting the address list…';
    if (job.status === 'fetching') return 'Retrieving property data…';
    if (job.status === 'parsing') return 'Reading the report…';
    return '';
}

function slimForSession(jobs) {
    return jobs
        .filter((j) => j.status === 'done' || j.status === 'error' || j.status === 'confirm' || j.status === 'queued')
        .slice(-16)
        .map((j) => ({
            id: j.id,
            query: j.query,
            provider: j.provider,
            owner: j.owner,
            userId: j.userId,
            leadId: j.leadId,
            propertyId: j.propertyId,
            reportAddress: j.reportAddress,
            reopenPath: j.reopenPath,
            status: j.status,
            step: j.step || null,
            message: j.message,
            nearby: j.nearby || [],
            savedFile: (j.result && j.result.savedFile) || j.savedFile || null,
            target: j.target || null,
        }));
}

function appendOwnerJob(prev, job) {
    const owner = job.owner || 'prospecting';
    const query = job.query;
    return [
        ...(prev || []).filter((j) => {
            if (!j || j.id === job.id) return false;
            const sameOwner = !j.owner || j.owner === owner;
            if (sameOwner && sameCapturePlace({ address: j.query }, { address: query })) {
                if (j.status === 'fetching' || j.status === 'parsing') return true;
                return false;
            }
            return true;
        }),
        job,
    ];
}

function readSessionJobs() {
    try {
        const raw = sessionStorage.getItem(SESSION_KEY);
        const list = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(list)) return [];
        return list.map((j) => {
            const sourceError = propertyReportSourceError(j.provider);
            return {
                ...j,
                nearby: sourceError ? [] : (Array.isArray(j.nearby) ? j.nearby : []),
                ...(sourceError ? { status: 'error', message: { tone: 'warn', text: sourceError.message } } : {}),
                consumed: false,
                result: null,
            };
        });
    } catch (_) {
        return [];
    }
}

export function LightstoneRetrievalProvider({ children }) {
    const [jobs, setJobs] = useState(() => (typeof window === 'undefined' ? [] : readSessionJobs()));
    const [notice, setNotice] = useState(null);
    const [wantReopen, setWantReopen] = useState(false);
    const [reopenJobId, setReopenJobId] = useState(null);
    const jobsRef = useRef(jobs);
    const runningIds = useRef(new Set());

    useEffect(() => {
        jobsRef.current = jobs;
        // Completion patches settle on the next render. Pump from that settled
        // state, otherwise finally still sees the previous job as fetching.
        pumpRef.current();
    }, [jobs]);
    useEffect(() => {
        try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(slimForSession(jobs))); } catch (_) { /* ignore */ }
    }, [jobs]);

    const patchJob = useCallback((id, patch) => {
        setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, ...patch } : j)));
    }, []);

    const pumpRef = useRef(() => {});

    const runFetch = useCallback(async (job) => {
        if (!job || runningIds.current.has(job.id)) return;
        runningIds.current.add(job.id);
        patchJob(job.id, { status: 'fetching', startedAt: job.startedAt || Date.now() });
        try {
            const leadId = job.leadId != null && String(job.leadId).trim() ? String(job.leadId).trim() : undefined;
            const res = await api.post(`${LIGHTSTONE_FETCH_BASE}/api/lightstone/fetch-report`, {
                provider: job.provider === undefined ? CURRENT_PROPERTY_REPORT_SOURCE.id : job.provider,
                userId: job.userId || undefined,
                leadId,
                address: job.query,
                search: job.search || undefined,
                picked: Boolean(job.picked),
            }, { timeout: FETCH_TIMEOUT_MS });
            const data = res.data || {};
            if (!data.ok) {
                const hits = nearbyFromPayload(data);
                const why = sanitizeRetrieveCopy(data.message || data.detail || 'Automated retrieval failed.');
                if (hits.length) {
                    patchJob(job.id, {
                        status: 'confirm',
                        nearby: hits,
                        result: null,
                        message: {
                            tone: 'ok',
                            text: 'Pick the matching property below, then we retrieve the report.',
                        },
                    });
                    if ((job.owner || '') === 'cma') {
                        setReopenJobId(job.id);
                        setWantReopen(true);
                    }
                } else {
                    patchJob(job.id, {
                        status: 'error',
                        nearby: hits,
                        result: null,
                        message: { tone: 'warn', text: withPdfFallback(why) },
                    });
                }
            } else {
                patchJob(job.id, {
                    status: 'done',
                    nearby: [],
                    result: data,
                    savedFile: data.savedFile || null,
                    consumed: false,
                    message: { tone: 'ok', text: job.doneText || 'Property report ready — click to review.' },
                });
            }
        } catch (err) {
            const body = err?.response?.data || {};
            const hits = nearbyFromPayload(body);
            const msg = withPdfFallback(body.message || body.error || err?.message || 'Automated retrieval failed.');
            if (hits.length) {
                patchJob(job.id, {
                    status: 'confirm',
                    nearby: hits,
                    result: null,
                    message: { tone: 'ok', text: 'Pick the matching property below, then we retrieve the report.' },
                });
                if ((job.owner || '') === 'cma') {
                    setReopenJobId(job.id);
                    setWantReopen(true);
                }
            } else {
                patchJob(job.id, {
                    status: 'error',
                    nearby: hits,
                    result: null,
                    message: { tone: 'warn', text: msg },
                });
            }
        } finally {
            runningIds.current.delete(job.id);
            pumpRef.current();
        }
    }, [patchJob]);

    const startAutoFetch = useCallback(async ({ query: q, userId, leadId, propertyId, reportAddress, target: tgt, owner: own, reopenPath: rp, doneText, picked, search: searchQ, provider }) => {
        const sourceError = propertyReportSourceError(provider);
        if (sourceError) {
            setNotice({ tone: 'warn', text: sourceError.message });
            return sourceError;
        }
        const query = String(q || '').trim();
        const search = String(searchQ || '').trim();
        const owner = own || 'prospecting';
        const outside = lightstoneCoverageRejectReason(query);
        if (outside) {
            setJobs((prev) => [...prev, {
                id: newJobId(),
                query,
                userId,
                leadId,
                propertyId,
                reportAddress:reportAddress || query,
                owner,
                reopenPath: rp || '/prospecting',
                target: tgt || null,
                status: 'error',
                message: { tone: 'warn', text: outside },
                nearby: [],
                result: null,
                savedFile: null,
                consumed: false,
                doneText,
                picked: Boolean(picked),
            }]);
            return;
        }
        if (!query) return;
        const already = jobsRef.current.some((j) => sameCapturePlace({ address: j.query }, { address: query }) && jobIsActive(j) && j.step === 'fetch');
        if (already) return;

        const inflight = jobsRef.current.filter((j) => j.status === 'fetching' || j.status === 'suggesting' || runningIds.current.has(j.id)).length;
        const status = inflight >= MAX_PARALLEL ? 'queued' : 'fetching';
        const job = {
            id: newJobId(),
            provider: provider === undefined ? CURRENT_PROPERTY_REPORT_SOURCE.id : provider,
            query,
            userId,
            leadId,
            propertyId,
            reportAddress:reportAddress || query,
            owner,
            reopenPath: rp || '/prospecting',
            target: tgt || null,
            status,
            step: 'fetch',
            message: null,
            nearby: [],
            result: null,
            savedFile: null,
            consumed: false,
            doneText,
            picked: Boolean(picked),
            search: search || undefined,
            startedAt: status === 'fetching' ? Date.now() : null,
        };
        setNotice(null);
        setJobs((prev) => appendOwnerJob(prev, job));
        if (status === 'fetching') runFetch(job);
        else pumpRef.current();
    }, [runFetch]);

    const runSuggest = useCallback(async (job) => {
        if (!job || runningIds.current.has(job.id)) return;
        runningIds.current.add(job.id);
        patchJob(job.id, { status: 'suggesting', startedAt: job.startedAt || Date.now() });
        try {
            const res = await api.post(`${LIGHTSTONE_FETCH_BASE}/api/lightstone/suggest`, {
                provider: job.provider === undefined ? CURRENT_PROPERTY_REPORT_SOURCE.id : job.provider,
                address: job.query,
            }, { timeout: FETCH_TIMEOUT_MS });
            const data = res.data || {};
            const hits = nearbyFromPayload(data);
            if (hits.length) {
                patchJob(job.id, {
                    status: 'confirm',
                    nearby: hits,
                    result: null,
                    message: {
                        tone: 'ok',
                        text: 'Pick the matching property below, then we retrieve the report.',
                    },
                });
                if ((job.owner || '') === 'cma') {
                    setReopenJobId(job.id);
                    setWantReopen(true);
                }
            } else {
                const why = withPdfFallback(data.message || data.detail || 'No matching properties came up.');
                patchJob(job.id, {
                    status: 'error',
                    nearby: [],
                    result: null,
                    message: { tone: 'warn', text: why },
                });
            }
        } catch (err) {
            const body = err?.response?.data || {};
            const hits = nearbyFromPayload(body);
            const msg = withPdfFallback(body.message || body.error || err?.message || 'Could not find matching properties.');
            patchJob(job.id, {
                status: hits.length ? 'confirm' : 'error',
                nearby: hits,
                result: null,
                message: {
                    tone: hits.length ? 'ok' : 'warn',
                    text: hits.length
                        ? 'Pick the matching property below, then we retrieve the report.'
                        : msg,
                },
            });
            if (hits.length && (job.owner || '') === 'cma') {
                setReopenJobId(job.id);
                setWantReopen(true);
            }
        } finally {
            runningIds.current.delete(job.id);
            pumpRef.current();
        }
    }, [patchJob]);

    const startSuggest = useCallback(async ({ query: q, userId, leadId, propertyId, reportAddress, target: tgt, owner: own, reopenPath: rp, doneText, provider }) => {
        const sourceError = propertyReportSourceError(provider);
        if (sourceError) {
            setNotice({ tone: 'warn', text: sourceError.message });
            return sourceError;
        }
        const query = String(q || '').trim();
        const owner = own || 'prospecting';
        const outside = lightstoneCoverageRejectReason(query);
        if (outside) {
            setJobs((prev) => [...prev, {
                id: newJobId(),
                query,
                userId,
                leadId,
                propertyId,
                reportAddress:reportAddress || query,
                owner,
                reopenPath: rp || '/prospecting',
                target: tgt || null,
                status: 'error',
                step: 'suggest',
                message: { tone: 'warn', text: outside },
                nearby: [],
                result: null,
                savedFile: null,
                consumed: false,
                doneText,
            }]);
            return;
        }
        if (!query) return;
        const already = jobsRef.current.some((j) => sameCapturePlace({ address: j.query }, { address: query }) && jobIsActive(j));
        if (already) return;

        const inflight = jobsRef.current.filter((j) => j.status === 'fetching' || j.status === 'suggesting' || runningIds.current.has(j.id)).length;
        const status = inflight >= MAX_PARALLEL ? 'queued' : 'suggesting';
        const job = {
            id: newJobId(),
            provider: provider === undefined ? CURRENT_PROPERTY_REPORT_SOURCE.id : provider,
            query,
            userId,
            leadId,
            propertyId,
            reportAddress:reportAddress || query,
            owner,
            reopenPath: rp || '/prospecting',
            target: tgt || null,
            status,
            step: 'suggest',
            message: null,
            nearby: [],
            result: null,
            savedFile: null,
            consumed: false,
            doneText,
            startedAt: status === 'suggesting' ? Date.now() : null,
        };
        setNotice(null);
        setJobs((prev) => appendOwnerJob(prev, job));
        if (status === 'suggesting') runSuggest(job);
        else pumpRef.current();
    }, [runSuggest]);

    pumpRef.current = () => {
        const list = jobsRef.current;
        const inflight = list.filter((j) => j.status === 'fetching' || j.status === 'suggesting' || runningIds.current.has(j.id)).length;
        const next = list.find((j) => j.status === 'queued' && !runningIds.current.has(j.id));
        if (next && inflight < MAX_PARALLEL) {
            if (next.step === 'suggest') runSuggest(next);
            else runFetch(next);
        }
    };

    const parsePdf = useCallback(async ({ file, query: q, userId, leadId, propertyId, reportAddress, target: tgt, owner: own, reopenPath: rp, doneText }) => {
        if (!file) return;
        const id = newJobId();
        const job = {
            id,
            query: q || file.name,
            userId,
            leadId,
            propertyId,
            reportAddress:reportAddress || q,
            owner: own || 'prospecting',
            reopenPath: rp || '/prospecting',
            target: tgt || null,
            status: 'parsing',
            message: null,
            nearby: [],
            result: null,
            savedFile: null,
            consumed: false,
            doneText,
            startedAt: Date.now(),
        };
        setNotice(null);
        setJobs((prev) => [...prev, job]);
        try {
            const content = await fileToBase64(file);
            const linkedLeadId = leadId != null && String(leadId).trim() ? String(leadId).trim() : undefined;
            const res = await api.post('/api/lightstone/parse-report', {
                file: { name: file.name, content },
                userId: userId || undefined,
                leadId: linkedLeadId,
                address: q || undefined,
                save: true,
            });
            if (res.data?.ok === false) {
                patchJob(id, { status: 'error', result: null, message: { tone: 'warn', text: res.data.message || res.data.error || 'Could not read the report.' } });
                return;
            }
            patchJob(id, {
                status: 'done',
                result: res.data || {},
                savedFile: (res.data && res.data.savedFile) || null,
                consumed: false,
                message: { tone: 'ok', text: doneText || 'Property report ready — click to review.' },
            });
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Could not read the report.';
            patchJob(id, { status: 'error', message: { tone: 'warn', text: msg } });
        }
    }, [patchJob]);

    const reparse = useCallback(async ({ fileId, userId, leadId, propertyId, reportAddress, target: tgt, owner: own, reopenPath: rp, doneText, query: q }) => {
        if (!fileId) return;
        const id = newJobId();
        const job = {
            id,
            query: q || 'Saved report',
            userId,
            leadId,
            propertyId,
            reportAddress:reportAddress || q,
            owner: own || 'prospecting',
            reopenPath: rp || '/prospecting',
            target: tgt || null,
            status: 'parsing',
            message: null,
            nearby: [],
            result: null,
            savedFile: { id: fileId },
            consumed: false,
            doneText,
            startedAt: Date.now(),
        };
        setNotice(null);
        setJobs((prev) => [...prev, job]);
        try {
            const res = await api.post('/api/lightstone/reparse', { fileId, userId: userId || undefined });
            const data = res.data || {};
            if (data.ok === false) {
                patchJob(id, { status: 'error', message: { tone: 'warn', text: data.message || 'Could not read the saved report.' } });
            } else {
                patchJob(id, {
                    status: 'done',
                    result: data,
                    savedFile: data.savedFile || { id: fileId },
                    consumed: false,
                    message: { tone: 'ok', text: doneText || 'Saved property report ready — click to review.' },
                });
            }
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Could not read the saved report.';
            patchJob(id, { status: 'error', message: { tone: 'warn', text: msg } });
        }
    }, [patchJob]);

    const dismiss = useCallback((jobId) => {
        setJobs((prev) => (jobId ? prev.filter((j) => j.id !== jobId) : prev.slice(0, -1)));
        if (jobId && reopenJobId === jobId) {
            setWantReopen(false);
            setReopenJobId(null);
        }
    }, [reopenJobId]);

    const dismissJobs = useCallback((ids) => {
        const set = new Set((ids || []).filter(Boolean));
        if (!set.size) return;
        setJobs((prev) => prev.filter((j) => !set.has(j.id)));
        if (reopenJobId && set.has(reopenJobId)) {
            setWantReopen(false);
            setReopenJobId(null);
        }
    }, [reopenJobId]);

    const clearIdleJobs = useCallback((owner) => {
        setJobs((prev) => prev.filter((j) => {
            if (owner && j.owner && j.owner !== owner) return true;
            return j.status === 'fetching' || j.status === 'parsing' || j.status === 'suggesting';
        }));
        setNotice(null);
    }, []);

    const requestReopen = useCallback((jobId) => {
        setReopenJobId(jobId);
        setWantReopen(true);
    }, []);
    const clearReopen = useCallback(() => {
        setWantReopen(false);
        setReopenJobId(null);
    }, []);
    const markConsumed = useCallback((jobId) => {
        patchJob(jobId, { consumed: true });
    }, [patchJob]);
    const updateJobTarget = useCallback((jobId, target) => {
        if (!jobId) return;
        patchJob(jobId, { target: target || null });
    }, [patchJob]);

    const reopenJob = jobs.find((j) => j.id === reopenJobId) || null;
    const latest = jobs[jobs.length - 1] || null;

    const value = useMemo(() => ({
        jobs,
        latest,
        status: latest ? latest.status : 'idle',
        busy: jobs.some(jobIsActive),
        message: notice || (latest && latest.message) || null,
        setMessage: setNotice,
        nearby: (latest && latest.nearby) || [],
        query: latest ? latest.query : '',
        pillOpen: jobs.length > 0,
        result: latest && latest.result ? latest.result : null,
        resultVersion: jobs.filter((j) => j.status === 'done' && j.result).length,
        target: (reopenJob && reopenJob.target) || (latest && latest.target) || null,
        wantReopen,
        reopenJob,
        owner: (reopenJob && reopenJob.owner) || (latest && latest.owner) || null,
        reopenPath: (reopenJob && reopenJob.reopenPath) || (latest && latest.reopenPath) || '/prospecting',
        startAutoFetch,
        startSuggest,
        parsePdf,
        reparse,
        dismiss,
        dismissJobs,
        clearIdleJobs,
        requestReopen,
        clearReopen,
        markConsumed,
        updateJobTarget,
        patchJob,
    }), [jobs, latest, notice, wantReopen, reopenJob, startAutoFetch, startSuggest, parsePdf, reparse, dismiss, dismissJobs, clearIdleJobs, requestReopen, clearReopen, markConsumed, updateJobTarget, patchJob]);

    return (
        <LightstoneRetrievalContext.Provider value={value}>
            {children}
            <LightstoneJobStack />
        </LightstoneRetrievalContext.Provider>
    );
}

function jobNeedsConfirm(job) {
    return job && (job.status === 'confirm' || (job.status === 'error' && Array.isArray(job.nearby) && job.nearby.length));
}

function queueAhead(jobs, job) {
    if (!job || job.status !== 'queued') return 0;
    return (jobs || []).filter((j) => j.status === 'queued' && j.id !== job.id)
        .filter((j) => (jobs || []).indexOf(j) < (jobs || []).indexOf(job)).length;
}

function jobTitle(job, jobs) {
    if (job.status === 'queued') {
        const ahead = queueAhead(jobs, job);
        return ahead <= 0 ? 'Waiting — next up' : `Waiting — ${ahead} ahead`;
    }
    if (job.status === 'suggesting') return 'Getting the address list';
    if (jobNeedsConfirm(job)) return 'Pick the matching property';
    if (job.status === 'fetching') return 'Retrieving property data';
    if (job.status === 'parsing') return 'Reading the report';
    if (job.status === 'done') return 'Property report ready';
    if (job.status === 'error') return 'Retrieval had an issue';
    return 'Property report';
}

function jobShowsOnStack(job) {
    if (!job) return false;
    // Address pick lives in the capture window — do not stack a second "Confirm the property" card.
    if (jobNeedsConfirm(job)) return false;
    return true;
}

function LightstoneJobStack() {
    const ls = useContext(LightstoneRetrievalContext);
    const navigate = useNavigate();
    const location = useLocation();
    const [collapsed, setCollapsed] = useState(() => new Set());
    const visible = (ls && ls.jobs ? ls.jobs : []).filter(jobShowsOnStack);
    if (typeof document === 'undefined' || !ls || !visible.length) return null;

    const reopen = (job) => {
        ls.requestReopen(job.id);
        if (!job.reopenPath) return;
        const pathOnly = String(job.reopenPath).split('?')[0];
        const here = `${location.pathname}${location.search || ''}`;
        const samePlace = here === job.reopenPath || location.pathname === pathOnly;
        navigate(job.reopenPath, { replace: samePlace });
    };
    const toggleCollapse = (id) => {
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    return createPortal(
        <div className="ls-job-stack" aria-live="polite">
            {visible.map((job) => {
                const busy = jobIsActive(job);
                const confirm = jobNeedsConfirm(job);
                const failed = job.status === 'error' && !confirm;
                const done = job.status === 'done';
                const isCollapsed = collapsed.has(job.id);
                const accent = failed ? '#a4260e' : '#10575c';
                const detail = busy
                    ? (job.query || 'This property')
                    : confirm
                        ? (job.query ? `${job.query} — pick an address, then we retrieve the report` : 'Pick an address from the list, then we retrieve the report')
                        : ((job.message && job.message.text) || job.query || 'Property report');
                return (
                    <div
                        key={job.id}
                        role="button"
                        tabIndex={0}
                        className={`ls-job-pill${failed ? ' is-error' : ''}${done ? ' is-done' : ''}${isCollapsed ? ' is-collapsed' : ''}`}
                        onClick={() => reopen(job)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') reopen(job); }}
                        title="Open this capture"
                        style={{ borderLeftColor: accent }}
                    >
                        <div className="ls-job-pill__body">
                            <div className="ls-job-pill__title">{jobTitle(job, ls.jobs)}</div>
                            {!isCollapsed && <div className="ls-job-pill__detail">{detail}</div>}
                            {!isCollapsed && busy && job.status !== 'queued' ? <LightstoneProgressBar active startedAt={job.startedAt} label="" tone="light" /> : null}
                        </div>
                        <button
                            type="button"
                            className="ls-job-pill__min"
                            onClick={(e) => { e.stopPropagation(); toggleCollapse(job.id); }}
                            aria-label={isCollapsed ? 'Expand' : 'Minimize'}
                            title={isCollapsed ? 'Expand' : 'Minimize'}
                        >{isCollapsed ? '▸' : '▾'}</button>
                        <button
                            type="button"
                            className="ls-job-pill__x"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (busy) toggleCollapse(job.id);
                                else ls.dismiss(job.id);
                            }}
                            aria-label={busy ? 'Minimize' : 'Dismiss'}
                            title={busy ? 'Minimize — retrieval keeps running' : 'Dismiss'}
                        >×</button>
                    </div>
                );
            })}
        </div>,
        document.body,
    );
}

export function useLightstoneRetrieval() {
    const ctx = useContext(LightstoneRetrievalContext);
    if (!ctx) throw new Error('useLightstoneRetrieval must be used within LightstoneRetrievalProvider');
    return ctx;
}

export function jobsForOwner(jobs, owner) {
    return (jobs || []).filter((j) => !owner || j.owner === owner);
}
